import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import { useAuth } from '@chainlit/react-client';

import { apiClient } from '@/api';
import { attachmentsState, type IAttachment } from '@/state/chat';
import { useTranslation } from '@/components/i18n/Translator';

// Google Drive, one click: the first time, a small Google popup grants
// drive.file and the server keeps the (encrypted) refresh token. Every later
// click mints an access token server-side and opens the Picker straight away
// — no popup, no redirect, no Settings round trip. Picked files are
// downloaded in the browser and attached to the message like any upload;
// no file is stored or indexed.

// Namespaces the placeholder chips so they can be found and dropped again
// without touching the real attachments beside them.
const PENDING_CHIP_PREFIX = 'drive-pending:';

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

declare global {
  interface Window {
    gapi?: {
      load(name: string, options: { callback: () => void }): void;
    };
    google?: {
      picker?: {
        PickerBuilder: new () => any;
        DocsView: new (viewId?: string) => any;
        ViewId: { DOCS: string };
        Action: { PICKED: string; CANCEL: string };
        Feature: { MULTISELECT_ENABLED: string };
      };
      accounts?: {
        oauth2: {
          initCodeClient(config: {
            client_id: string;
            scope: string;
            ux_mode: 'popup';
            hint?: string;
            prompt?: string;
            callback: (resp: { code?: string; error?: string }) => void;
            error_callback?: (err: { type?: string }) => void;
          }): { requestCode(): void };
        };
      };
    };
  }
}

// Build-time keys. Only a fallback: /api/drive/status serves the same keys at
// runtime and wins when present.
const BUILT_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || '';
const BUILT_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// Picker setAppId expects the Cloud project *number*, not the OAuth client id.
const appIdFromClientId = (clientId: string) =>
  (clientId.split('-')[0] || '').replace(/\D/g, '');

const GOOGLE_EXPORT_AS_PDF = new Set([
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.presentation',
]);

const PICKER_MIME_TYPES =
  'application/pdf,image/jpeg,image/png,application/vnd.google-apps.document,application/vnd.google-apps.presentation';
const FOLDER_MIME = 'application/vnd.google-apps.folder';

type DriveDoc = { id: string; name: string; mimeType: string };

function loadScript(src: string, marker: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-fongpt="${marker}"]`,
    );
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () =>
          reject(new Error(`${src} failed to load`)),
        );
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.fongpt = marker;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`${src} failed to load`));
    document.head.appendChild(script);
  });
}

async function loadGoogleLibraries(): Promise<void> {
  await Promise.all([
    loadScript('https://accounts.google.com/gsi/client', 'gsi'),
    loadScript('https://apis.google.com/js/api.js', 'gapi').then(
      () =>
        new Promise<void>((resolve, reject) => {
          if (window.google?.picker) return resolve();
          if (!window.gapi?.load) {
            return reject(new Error('Google API failed to load'));
          }
          window.gapi.load('picker', { callback: () => resolve() });
        }),
    ),
  ]);
  if (!window.google?.picker || !window.google?.accounts?.oauth2) {
    throw new Error('Google libraries failed to load');
  }
}

function attachmentName(name: string, mimeType: string): string {
  const base = (name || 'drive-file').trim() || 'drive-file';
  if (GOOGLE_EXPORT_AS_PDF.has(mimeType) && !/\.pdf$/i.test(base)) {
    return `${base}.pdf`;
  }
  return base;
}

/** Thrown with Google's own reason so the toast can say what actually failed. */
async function downloadDriveFile(
  accessToken: string,
  doc: DriveDoc,
): Promise<File> {
  const id = encodeURIComponent(doc.id);
  const exportAsPdf = GOOGLE_EXPORT_AS_PDF.has(doc.mimeType);
  const url = exportAsPdf
    ? `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=${encodeURIComponent('application/pdf')}`
    : `https://www.googleapis.com/drive/v3/files/${id}?alt=media&supportsAllDrives=true`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    let reason = '';
    try {
      const body = await res.json();
      reason = body?.error?.errors?.[0]?.reason || body?.error?.message || '';
    } catch {
      /* not JSON */
    }
    const err = new Error(reason || String(res.status));
    (err as Error & { status?: number; reason?: string }).status = res.status;
    (err as Error & { status?: number; reason?: string }).reason = reason;
    throw err;
  }
  const blob = await res.blob();
  const type = exportAsPdf
    ? 'application/pdf'
    : doc.mimeType || blob.type || 'application/octet-stream';
  return new File([blob], attachmentName(doc.name, doc.mimeType), { type });
}

interface PickerKeys {
  apiKey: string;
  clientId: string;
}

interface Options {
  disabled?: boolean;
  onFileUpload: (
    files: File[],
    meta?: { source?: 'upload' | 'drive' },
  ) => void;
  onFileUploadError: (error: string) => void;
}

/**
 * Returns ``open`` — call it straight from a click handler. The token popup
 * must be requested inside the user gesture or Safari blocks it, so the
 * libraries and keys are preloaded on mount.
 */
export function useGoogleDrivePicker({
  disabled = false,
  onFileUpload,
  onFileUploadError,
}: Options) {
  const { i18n } = useTranslation();
  const en = i18n.language.toLowerCase().startsWith('en');
  const copy = useMemo(
    () =>
      en
        ? {
            selectFiles: 'Select files',
            stageFetching: 'Fetching from Drive…',
            notConfigured: 'Google Drive is not configured on this server.',
            loading: 'Google Drive is still loading — try again in a moment.',
            popupBlocked:
              'The browser blocked the Google sign-in window. Allow pop-ups for this site and try again.',
            authFailed: 'Google Drive access was not granted.',
            clickAgain: 'Google Drive needs to be connected again — click Google Drive once more.',
            pickerFailed: 'Failed to open Google Drive.',
            noAccess: (name: string) =>
              `No access to “${name}”. Pick it again from Drive.`,
            ownerBlocked: (name: string) =>
              `The owner of “${name}” has disabled downloading, so it can't be added. Make your own copy and pick that.`,
            downloadFailed: (name: string, reason: string) =>
              `Failed to add “${name}” from Drive (${reason}).`,
          }
        : {
            selectFiles: 'Izaberi fajlove',
            stageFetching: 'Preuzimam sa Drive-a…',
            notConfigured: 'Google Drive nije konfigurisan na serveru.',
            loading: 'Google Drive se još učitava — pokušaj ponovo za trenutak.',
            popupBlocked:
              'Browser je blokirao Google prozor za prijavu. Dozvoli iskačuće prozore za ovaj sajt i pokušaj ponovo.',
            authFailed: 'Pristup Google Drive-u nije odobren.',
            clickAgain: 'Google Drive treba ponovo povezati — klikni još jednom na Google Drive.',
            pickerFailed: 'Neuspelo otvaranje Google Drive-a.',
            noAccess: (name: string) =>
              `Nema pristupa fajlu „${name}”. Izaberi ga ponovo sa Drive-a.`,
            ownerBlocked: (name: string) =>
              `Vlasnik fajla „${name}” je zabranio preuzimanje, pa ne može da se doda. Napravi svoju kopiju i izaberi nju.`,
            downloadFailed: (name: string, reason: string) =>
              `Neuspelo dodavanje „${name}” sa Drive-a (${reason}).`,
          },
    [en],
  );

  const setAttachments = useSetRecoilState(attachmentsState);
  // Logged-in email, so the one-time Google popup skips the account chooser
  // for people signed into several Google accounts.
  const { user } = useAuth();
  const loginHint =
    user?.identifier && user.identifier.includes('@')
      ? user.identifier
      : undefined;
  const [keys, setKeys] = useState<PickerKeys | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const tokenRef = useRef<{ value: string; expiresAt: number } | null>(null);
  // Server holds a usable Drive grant for this user: no popup needed.
  const connectedRef = useRef(false);
  // Google omitted the refresh token last time; force the consent screen.
  const forceConsentRef = useRef(false);
  const pickerOpenRef = useRef(false);

  // Keys + Google libraries, ahead of the click.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let apiKey = BUILT_API_KEY;
      let clientId = BUILT_CLIENT_ID;
      try {
        const res = await fetch(apiClient.buildEndpoint('/api/drive/status'), {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          apiKey = data?.picker_api_key || apiKey;
          clientId = data?.picker_client_id || clientId;
          connectedRef.current = !!data?.connected;
        }
      } catch {
        /* fall back to build-time keys */
      }
      if (cancelled) return;
      if (!apiKey || !clientId) {
        setKeys(null);
        return;
      }
      setKeys({ apiKey, clientId });
      try {
        await loadGoogleLibraries();
      } catch {
        return; // reported on click
      }
      if (cancelled) return;
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const showPendingChips = useCallback(
    (docs: DriveDoc[]) => {
      setAttachments((prev) => [
        ...prev,
        ...docs.map<IAttachment>((doc) => ({
          id: PENDING_CHIP_PREFIX + doc.id,
          name: doc.name,
          size: 0,
          type: doc.mimeType,
          uploaded: false,
          source: 'drive',
          stage: copy.stageFetching,
        })),
      ]);
    },
    [setAttachments, copy.stageFetching],
  );

  const clearPendingChips = useCallback(() => {
    setAttachments((prev) =>
      prev.filter((a) => !a.id.startsWith(PENDING_CHIP_PREFIX)),
    );
  }, [setAttachments]);

  const attachDocs = useCallback(
    async (token: string, docs: DriveDoc[]) => {
      showPendingChips(docs);
      const results = await Promise.allSettled(
        docs.map((doc) => downloadDriveFile(token, doc)),
      );
      clearPendingChips();
      const files: File[] = [];
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          files.push(result.value);
          return;
        }
        const doc = docs[i];
        const err = result.reason as Error & { reason?: string };
        const reason = err?.reason || err?.message || '';
        if (reason === 'cannotExportFile' || reason === 'cannotDownloadFile') {
          onFileUploadError(copy.ownerBlocked(doc.name));
        } else if (
          reason === 'appNotAuthorizedToFile' ||
          reason === 'insufficientFilePermissions'
        ) {
          onFileUploadError(copy.noAccess(doc.name));
        } else {
          onFileUploadError(copy.downloadFailed(doc.name, reason));
        }
      });
      if (files.length > 0) onFileUpload(files, { source: 'drive' });
    },
    [showPendingChips, clearPendingChips, onFileUpload, onFileUploadError, copy],
  );

  const showPicker = useCallback(
    (token: string) => {
      const pickerApi = window.google?.picker;
      if (!pickerApi || !keys) {
        setBusy(false);
        onFileUploadError(copy.pickerFailed);
        return;
      }
      try {
        const docsView = (configure: (view: any) => void, types: string) => {
          const view = new pickerApi.DocsView(pickerApi.ViewId.DOCS);
          view.setMimeTypes(types);
          configure(view);
          return view;
        };
        // setMimeTypes filters folders out too, so views that walk folders
        // must let the folder type back in (folders stay unselectable).
        const browsable = `${PICKER_MIME_TYPES},${FOLDER_MIME}`;
        const myDrive = docsView((v) => {
          v.setOwnedByMe(true);
          v.setIncludeFolders(true);
          v.setParent('root');
        }, browsable);
        const sharedWithMe = docsView((v) => {
          v.setOwnedByMe(false);
          v.setIncludeFolders(true);
        }, browsable);
        const recent = docsView(() => {}, PICKER_MIME_TYPES);

        // Google leaves the picker (and its spinner) on screen after PICKED
        // until the host disposes of it, so teardown is ours.
        let closePicker = () => {};
        const builder = new pickerApi.PickerBuilder()
          .setTitle(copy.selectFiles)
          .setOAuthToken(token)
          .setDeveloperKey(keys.apiKey)
          .setAppId(appIdFromClientId(keys.clientId) || keys.clientId)
          .addView(myDrive)
          .addView(sharedWithMe)
          .addView(recent)
          .setCallback(async (data: { action: string; docs?: DriveDoc[] }) => {
            // Picker also fires 'loaded'; only PICKED and CANCEL dismiss.
            if (data.action === pickerApi.Action.CANCEL) {
              closePicker();
              return;
            }
            if (data.action !== pickerApi.Action.PICKED) return;
            closePicker();
            if (data.docs?.length) await attachDocs(token, data.docs);
          });
        if (pickerApi.Feature?.MULTISELECT_ENABLED) {
          builder.enableFeature(pickerApi.Feature.MULTISELECT_ENABLED);
        }
        // Some Safari loads omit the enum.
        builder.enableFeature('multiselectEnabled' as never);
        const picker = builder.build();

        // When Google errors inside its iframe it never fires CANCEL, leaving
        // a backdrop with nothing to click. Escape is the way out.
        const dismiss = (event: KeyboardEvent) => {
          if (event.key === 'Escape') closePicker();
        };
        closePicker = () => {
          document.removeEventListener('keydown', dismiss);
          pickerOpenRef.current = false;
          setBusy(false);
          try {
            picker.dispose();
          } catch {
            picker.setVisible(false);
          }
        };
        document.addEventListener('keydown', dismiss);
        pickerOpenRef.current = true;
        picker.setVisible(true);
      } catch {
        pickerOpenRef.current = false;
        setBusy(false);
        onFileUploadError(copy.pickerFailed);
      }
    },
    [keys, copy, attachDocs, onFileUploadError],
  );

  /** Access token minted server-side from the stored grant. */
  const fetchServerToken = useCallback(async (): Promise<string> => {
    const cached = tokenRef.current;
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    const res = await fetch(apiClient.buildEndpoint('/api/drive/token'), {
      credentials: 'include',
    });
    if (res.status === 401) {
      // Grant revoked or expired: the next click goes through the popup.
      connectedRef.current = false;
      tokenRef.current = null;
      throw new Error(copy.clickAgain);
    }
    const data = res.ok ? await res.json() : null;
    if (!data?.access_token) throw new Error(copy.authFailed);
    // Google access tokens live an hour; renew a little early.
    tokenRef.current = {
      value: data.access_token,
      expiresAt: Date.now() + 50 * 60 * 1000,
    };
    return data.access_token;
  }, [copy]);

  const openWithServerToken = useCallback(async () => {
    try {
      const token = await fetchServerToken();
      showPicker(token);
    } catch (err) {
      setBusy(false);
      onFileUploadError(err instanceof Error ? err.message : copy.authFailed);
    }
  }, [fetchServerToken, showPicker, onFileUploadError, copy]);

  const open = useCallback(() => {
    if (disabled || busy || pickerOpenRef.current) return;
    if (!keys) {
      onFileUploadError(copy.notConfigured);
      return;
    }
    if (!ready || !window.google?.accounts?.oauth2) {
      onFileUploadError(copy.loading);
      return;
    }
    setBusy(true);

    if (connectedRef.current) {
      // Let the menu that triggered this finish closing before the modal.
      window.setTimeout(() => void openWithServerToken(), 150);
      return;
    }

    // First time only. Built and requested synchronously inside the click —
    // Safari blocks a popup opened after any await.
    const codeClient = window.google.accounts.oauth2.initCodeClient({
      client_id: keys.clientId,
      scope: DRIVE_SCOPE,
      ux_mode: 'popup',
      hint: loginHint,
      ...(forceConsentRef.current ? { prompt: 'consent' } : {}),
      callback: async (resp) => {
        if (!resp.code) {
          setBusy(false);
          if (resp.error) onFileUploadError(copy.authFailed);
          return;
        }
        try {
          const res = await fetch(
            apiClient.buildEndpoint('/api/drive/oauth/code'),
            {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: resp.code }),
            },
          );
          if (res.status === 409) {
            // No refresh token came back; the next click shows consent.
            forceConsentRef.current = true;
            throw new Error(copy.clickAgain);
          }
          if (!res.ok) {
            let detail = copy.authFailed;
            try {
              const body = await res.json();
              if (body?.detail) detail = String(body.detail);
            } catch {
              /* not JSON */
            }
            throw new Error(detail);
          }
          connectedRef.current = true;
          forceConsentRef.current = false;
          await openWithServerToken();
        } catch (err) {
          setBusy(false);
          onFileUploadError(
            err instanceof Error ? err.message : copy.authFailed,
          );
        }
      },
      error_callback: (err) => {
        setBusy(false);
        // Closing the Google window is a plain cancel, not an error.
        if (err?.type === 'popup_closed') return;
        onFileUploadError(
          err?.type === 'popup_failed_to_open'
            ? copy.popupBlocked
            : copy.authFailed,
        );
      },
    });
    codeClient.requestCode();
  }, [
    disabled,
    busy,
    keys,
    ready,
    copy,
    loginHint,
    openWithServerToken,
    onFileUploadError,
  ]);

  return { open, busy, available: !!keys };
}

export default useGoogleDrivePicker;
